import { FeatureCollection, Polygon } from "geojson";
import { useCallback, useContext, useMemo, useState } from "react";

import { CityContext } from "contexts/city-context-provider";
import { GeoJsonLayer } from "@deck.gl/layers";
import { PickingInfo } from "deck.gl";
import { QUERY_CURB_ZONES_SHORTEN } from "@/services/web/api-curb-zone.const";
import { getPolygonColor } from "constants/map.const";
import { skipToken } from "@apollo/client/react";
import { useTrackedQuery } from "@/hooks/apollo-insights-hooks";
import { Filter } from "@/components/search-and-filter/SearchFilter.type";
import { CurbZonesShortenQuery } from "@/types/gql/graphql";
import { TooltipType } from "@/types/map.type";
import { isEmpty } from "@/helper/utils";

export type CurbZoneShorten = CurbZonesShortenQuery["curbZones"][number];

const CURB_ZONE_POLYGON_LAYER_ID = "curb-zone-polygon-background";

// Boost the alpha for this page only, without touching the shared policy colors used elsewhere.
const darkenFillColor = (color: [number, number, number, number]): [number, number, number, number] => {
  const [r, g, b, a] = color;
  return [r, g, b, Math.min(255, Math.round(a * 1.9))];
};

export const useCurbZonesLayer = (nearBy: number[][] | undefined, filter: Filter) => {
  const { currentCity } = useContext(CityContext);
  const [hoverInfo, setHoverInfo] = useState<TooltipType<CurbZoneShorten>>();

  const curbZonesOptions = useMemo(() => {
    if (currentCity?.id && nearBy) {
      return {
        variables: {
          filterInput: {
            cityId: currentCity.id,
            boundingbox: nearBy,
            policies: filter.selectedPolicies.length > 0 ? filter.selectedPolicies : undefined,
          },
        },
        actionName: "CurbZonesShorten",
        fetchPolicy: "no-cache" as const,
      };
    }
    return skipToken;
  }, [currentCity?.id, nearBy, filter.selectedPolicies]);

  const { data, previousData } = useTrackedQuery(QUERY_CURB_ZONES_SHORTEN, curbZonesOptions);

  const curbZonePolygons: FeatureCollection<Polygon> = useMemo(() => {
    // Fallback to previous data during loading
    const curbZones = data?.curbZones || previousData?.curbZones;

    if (!curbZones?.length) {
      return {
        type: "FeatureCollection",
        features: [],
      };
    }

    return {
      type: "FeatureCollection",
      features: curbZones.map((item) => ({
        type: "Feature" as const,
        geometry: item.geometry,
        properties: item,
        id: item.id,
      })),
    };
  }, [data?.curbZones, previousData?.curbZones]);

  // curb zones visuals
  const curbZonePolygonsLayer = useMemo(
    () =>
      new GeoJsonLayer({
        id: CURB_ZONE_POLYGON_LAYER_ID,
        data: curbZonePolygons,
        stroked: true,
        getFillColor: (feature) => darkenFillColor(getPolygonColor(feature, "fillColor")),
        getLineColor: (feature) => getPolygonColor(feature, "lineColor"),
        lineWidthMaxPixels: 1.6,
        visible: true,
        pickable: true,
      }),
    [curbZonePolygons],
  );

  const handleCurbZoneHover = useCallback(
    (info: PickingInfo) => {
      if (info.layer?.id === CURB_ZONE_POLYGON_LAYER_ID && info.object) {
        setHoverInfo({ x: info.x, y: info.y, properties: info.object.properties });
      } else if (!isEmpty(hoverInfo)) {
        setHoverInfo(undefined);
      }
    },
    [hoverInfo],
  );

  return { curbZonePolygonsLayer, hoverInfo, handleCurbZoneHover };
};
